/**
 * LIFECYCLE-1b on the overview header.
 * Start and Close: project manager, workspace owner, or admin.
 * End early and Reopen: an admin who belongs to the workspace.
 */
import { useState } from 'react';

import type { ProjectDetail } from '../projects.api';

import { StartDateDialog } from './StartDateDialog';

import { api } from '@/lib/api';
import { useAuth } from '@/state/AuthContext';
import { useWorkspaceRole } from '@/hooks/useWorkspaceRole';
import { isPlatformAdmin } from '@/utils/access';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useProjectContext } from '@/features/projects/layout/ProjectPageLayout';

const STATE_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  CLOSING: 'Closing',
  CLOSED: 'Closed',
};

type Action = 'close' | 'end-early' | 'reopen';

export function ProjectLifecycleControls({ project }: { project: ProjectDetail }) {
  const { user } = useAuth();
  const { role } = useWorkspaceRole(project.workspaceId);
  const { refresh } = useProjectContext();
  const [action, setAction] = useState<Action | null>(null);
  const [dateMode, setDateMode] = useState<'start' | 'replan' | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const state = project.state;
  if (!state || !STATE_LABEL[state]) return null;

  const isManager = Boolean(user?.id && project.projectManagerId === user.id);
  const canManage = role === 'OWNER' || role === 'ADMIN' || isManager;
  const canAdmin = role === 'ADMIN' || (isPlatformAdmin(user) && role !== 'GUEST' && role != null);

  const run = async (path: string, body?: { reason: string }) => {
    setError(null);
    try {
      if (body) await api.post(path, body);
      else await api.post(path);
      setAction(null);
      setReason('');
      await refresh();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { code?: string; message?: string } } })?.response?.data;
      setError(getApiErrorMessage(data) || 'Could not update the project.');
    }
  };

  const confirm = async () => {
    if (!action) return;
    if (!reason.trim()) {
      setError('Enter a reason.');
      return;
    }
    const path =
      action === 'close'
        ? `/work/projects/${project.id}/close`
        : action === 'end-early'
          ? `/work/projects/${project.id}/end-early`
          : `/work/projects/${project.id}/reopen`;
    await run(path, { reason: reason.trim() });
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="project-lifecycle">
      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-800">
        {STATE_LABEL[state]}
      </span>
      {/* DRAFT-TRUTH-1: Start picks the real start date (preview first); Re-plan
          moves a Draft's planned dates with no state change (gated or not). */}
      {state === 'DRAFT' && canManage ? (
        <>
          <button type="button" className="rounded border px-2 py-1 text-sm" onClick={() => { setDateMode('start'); setAction(null); setError(null); }}>
            Start
          </button>
          <button type="button" className="rounded border px-2 py-1 text-sm" onClick={() => { setDateMode('replan'); setAction(null); setError(null); }}>
            Re-plan start date
          </button>
        </>
      ) : null}
      {state === 'DRAFT' && canManage && dateMode ? (
        <StartDateDialog
          key={dateMode}
          projectId={project.id}
          mode={dateMode}
          onCancel={() => setDateMode(null)}
          onDone={async () => {
            setDateMode(null);
            await refresh();
          }}
        />
      ) : null}
      {state === 'ACTIVE' && canManage ? (
        <button type="button" className="rounded border px-2 py-1 text-sm" onClick={() => { setAction('close'); setError(null); setReason(''); }}>
          Close
        </button>
      ) : null}
      {/* ONE-TRUTH-FE-2: a Draft never offers End early (UI-only; a Draft is discarded via
          Delete/Archive; backend refusal is LIFECYCLE-DRAFT-END-1). */}
      {(state === 'ACTIVE' || state === 'CLOSING') && canAdmin ? (
        <button type="button" className="rounded border px-2 py-1 text-sm" onClick={() => { setAction('end-early'); setError(null); setReason(''); }}>
          End early
        </button>
      ) : null}
      {state === 'CLOSED' && canAdmin ? (
        <button type="button" className="rounded border px-2 py-1 text-sm" onClick={() => { setAction('reopen'); setError(null); setReason(''); }}>
          Reopen
        </button>
      ) : null}
      {action ? (
        <div className="flex w-full items-center gap-2">
          <label className="text-sm" htmlFor="lifecycle-reason">
            Reason
            <input
              id="lifecycle-reason"
              aria-label="Reason"
              className="ml-2 rounded border px-2 py-1"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button type="button" className="text-sm" onClick={() => void confirm()}>
            {action === 'close' ? 'Confirm close' : action === 'end-early' ? 'Confirm end early' : 'Confirm reopen'}
          </button>
        </div>
      ) : null}
      {error ? <p className="w-full text-sm text-red-700">{error}</p> : null}
    </div>
  );
}
